<script>
import CoreComponent from '../../../../../../../dow-platform/ui.frontend/src/dow-platform/components/core/CoreComponent.vue'

const UNDERLINE = {
  canvasHeightPx: 30,   // Canvas height in px
  topOffsetEm: -0.65,   // Distance from the text baseline (maps to canvas.style.bottom)
  arcPct: 0.45,         // Arc strength as a percent of canvas height. (Higher = more pronounced "smile" Range: 0.25 - 0.55)
  curlPct: 0.20,        // Curl amount at ends as a percent of canvas height (Higher = ends hook upward more. Range: 0.10 - 0.35)
  bias: 0.00            // Left/right bias for the curve and curl (0 = symmetric. +0.10 biases energy right, -0.10 biases left - Range: -0.15 - +0.15)
}

const STROKE = {
  widthPx: 2,
  microWidthPx: 1.2,
  microAlpha: 0.25,
  microMinWidthPx: 60
}

const LAYOUT = {
  overhangTotalPx: 24,
  insetStartPct: 0.04,
  insetEndPct: 0.05,
  insetStartMaxPx: 6,
  insetEndMaxPx: 10
}

const STATE = { installed: false, observer: null, raf: null }

function hashString(str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function mulberry32(a) {
  return function () {
    let t = (a += 0x6D2B79F5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function getStrokeColor(root, uEl) {
  const target = uEl || root
  const probe = root.querySelector('.embellished-underline-probe')
  if (!probe) return getComputedStyle(target).color || 'rgb(0,0,0)'

  const csProbe = getComputedStyle(probe)
  let c = csProbe.getPropertyValue('--underline-color').trim()
  if (!c || c === 'currentColor') c = getComputedStyle(target).color
  return c || 'rgb(0,0,0)'
}

function drawUnderline(root, canvas, uEl, seed, color) {
  const rect = uEl.getBoundingClientRect()
  const textW = Math.max(0, Math.round(rect.width))
  if (!textW) return false

  const rand = mulberry32(seed)
  const dpr = window.devicePixelRatio || 1

  const cssH = UNDERLINE.canvasHeightPx
  const pad = Math.round(LAYOUT.overhangTotalPx / 2)
  const cssW = textW + pad * 2

  canvas.width = Math.round(cssW * dpr)
  canvas.height = Math.round(cssH * dpr)

  canvas.style.left = `${-pad}px`
  canvas.style.width = `calc(100% + ${pad * 2}px)`
  canvas.style.height = `${cssH}px`
  canvas.style.bottom = `${UNDERLINE.topOffsetEm}em`

  const ctx = canvas.getContext('2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cssW, cssH)

  ctx.strokeStyle = color
  ctx.lineWidth = STROKE.widthPx
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const baseY = cssH * 0.70
  const arcHeight = cssH * UNDERLINE.arcPct
  const curlLift = cssH * UNDERLINE.curlPct
  const bias = Math.max(-0.3, Math.min(0.3, UNDERLINE.bias))
  const startInset = Math.min(LAYOUT.insetStartMaxPx, textW * LAYOUT.insetStartPct)
  const endInset = Math.min(LAYOUT.insetEndMaxPx, textW * LAYOUT.insetEndPct)

  const x0 = startInset
  const x1 = cssW - endInset
  const w = Math.max(1, x1 - x0)
  const cy = baseY - arcHeight
  const curlLen = w * (0.12 + Math.abs(bias) * 0.05)
  const c1x = x0 + curlLen
  const c1y = baseY - curlLift
  const c2x = x1 - curlLen
  const c2y = cy

  const endDrift = (rand() - 0.5) * 0.4

  ctx.beginPath()
  ctx.moveTo(x0, baseY)
  ctx.bezierCurveTo(c1x, c1y, c2x, c2y, x1, baseY + endDrift)
  ctx.stroke()

  if (cssW > STROKE.microMinWidthPx) {
    ctx.globalAlpha = STROKE.microAlpha
    ctx.lineWidth = STROKE.microWidthPx
    const micro = (rand() - 0.5) * 0.6

    ctx.beginPath()
    ctx.moveTo(x0, baseY + micro)
    ctx.bezierCurveTo(c1x, c1y + micro, c2x, c2y + micro, x1, baseY + endDrift + micro)
    ctx.stroke()

    ctx.globalAlpha = 1
  }

  return true
}

function processRoot(root) {
  if (!root || root.getAttribute('data-embellished-underline') !== 'true') return

  root.querySelectorAll('u').forEach((u) => {
    const txt = (u.textContent || '').trim()
    if (!txt) return

    if (!u.classList.contains('embellished')) u.classList.add('embellished')

    let canvas = u.querySelector('canvas.embellish-canvas')
    if (!canvas) {
      canvas = document.createElement('canvas')
      canvas.className = 'embellish-canvas'
      canvas.setAttribute('aria-hidden', 'true')
      canvas.tabIndex = -1
      u.appendChild(canvas)
    }

    const seed = hashString(txt)
    const color = getStrokeColor(root, u)
    const dpr = window.devicePixelRatio || 1

    const nextKey = `${seed}|${color}|${dpr}`
    if (canvas.dataset.underlineKey === nextKey) return
    canvas.dataset.underlineKey = nextKey

    drawUnderline(root, canvas, u, seed, color)
  })
}

function scanAll() {
  document
    .querySelectorAll('.cmp-corporate-title[data-embellished-underline="true"]')
    .forEach(processRoot)
}

function scheduleScan() {
  if (STATE.raf) cancelAnimationFrame(STATE.raf)
  STATE.raf = requestAnimationFrame(scanAll)
}

function installOnce() {
  if (STATE.installed) return
  STATE.installed = true

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleScan)
  } else {
    scheduleScan()
  }

  STATE.observer = new MutationObserver(scheduleScan)
  STATE.observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class']
  })

  window.addEventListener('resize', scheduleScan)
}

installOnce()

export default {
  extends: CoreComponent,
  mounted() {
    processRoot(this.$el)
  }
}
</script>

<style lang="scss">
@import './corporate-title.scss';
</style>